<script setup>
import { computed } from 'vue';
const props = defineProps({
  presence: { type: Object, required: true },
  showDetails: { type: Boolean, default: false },
});
const getWorkingLabel = computed(() => props.presence?.working && !props.presence?.onBreak ? 'Trabajando' : 'Trabajado');
const reasons = { break: 'Descanso', medical_appointment: 'Cita con el médico', child_conciliation: 'Conciliación de hijos', family_conciliation: 'Conciliación familiar', other: 'Otros' };
const secondsValue = value => {
  if (value === null || value === undefined || value === '') return null;
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
};
const formatTime = value => {
  const seconds = secondsValue(value);
  if (seconds === null) return '—';
  return String(Math.floor(seconds / 3600)).padStart(2, '0') + ':' + String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
};
const formatDate = value => {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleTimeString('es-ES');
};
const formatSegmentTime = value => {
  const seconds = secondsValue(value);
  if (seconds === null) return '—';
  return formatTime(seconds) + ':' + String(Math.floor(seconds % 60)).padStart(2, '0');
};
const presenceData = computed(() => (props.presence?.workSegments ?? []).map(segment => ({
  start: formatDate(segment.start), end: formatDate(segment.end),
  duration: formatSegmentTime(segment.delta),
})));
const breakData = computed(() => (props.presence?.breakSegments ?? []).map(segment => ({
  start: formatDate(segment.start),
  end: segment.end ? formatDate(segment.end) : 'En curso',
  duration: formatTime(segment.delta),
  reason: segment.pauseReasonOtherText || reasons[segment.pauseReason] || segment.pauseReason || '—',
})));
const totalBreak = computed(() => {
  const segments = props.presence?.breakSegments ?? [];
  if (!segments.length) return '';
  const durations = segments.map(segment => secondsValue(segment.delta));
  if (durations.some(duration => duration === null)) return '';
  return (props.presence?.onBreak ? 'Tiempo de parada: ' : 'Tiempo en parada: ') + formatTime(durations.reduce((sum, duration) => sum + duration, 0));
});
</script>

<template>
  <div class="presence-content">
    <el-divider v-if="presenceData.length" class="short-divider">{{ getWorkingLabel }}</el-divider>
    <el-table v-if="presenceData.length" :data="presenceData" border class="condensed-table">
      <el-table-column prop="start" label="Entrada" />
      <el-table-column prop="end" label="Salida" />
      <el-table-column v-if="showDetails" prop="duration" label="Duración" />
    </el-table>
    <span v-else>Sin datos de presencia</span>
    <el-divider v-if="breakData.length" class="short-divider">Paradas</el-divider>
    <template v-if="showDetails && breakData.length">
      <el-table :data="breakData" border class="condensed-table table-not-mobile presence-break-table">
        <el-table-column prop="start" label="Inicio" width="105" />
        <el-table-column prop="end" label="Fin" width="105" />
        <el-table-column prop="duration" label="Duración" width="105" />
        <el-table-column prop="reason" label="Motivo" min-width="120" />
      </el-table>
      <div class="table-mobile presence-break-cards">
        <el-card v-for="(segment, index) in breakData" :key="index" shadow="never" class="presence-break-card">
          <div><strong>Inicio:</strong> {{ segment.start }}</div>
          <div><strong>Fin:</strong> {{ segment.end }}</div>
          <div><strong>Duración:</strong> {{ segment.duration }}</div>
          <div><strong>Motivo:</strong> {{ segment.reason }}</div>
        </el-card>
      </div>
    </template>
    <p v-if="totalBreak" class="presence-break-total">{{ totalBreak }}</p>
  </div>
</template>

<style scoped>
.presence-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: -15px;
  min-width: 0;
  width: 100%;
}
.short-divider { width: 80%; margin: 20px auto; }
.presence-break-total { margin: 8px 0 0; text-align: center; }
.presence-break-cards { width: 100%; }
.presence-break-card { margin-bottom: 8px; overflow-wrap: anywhere; }
.presence-break-card strong { color: #474ce6; }
@media (min-width: 768px) { .presence-break-cards { display: none; } }
@media (max-width: 767px) { .presence-break-table { display: none; } }
</style>
